/**
 * Global Axios Interceptor for Automatic Authentication
 *
 * This interceptor automatically adds Auth0 tokens to all API requests,
 * eliminating the need for manual token management.
 */

import axios, { AxiosInstance } from "axios";

// Store the Auth0 token getter function
let getAccessTokenSilently: (() => Promise<string>) | null = null;

// Shared axios instance for app calls
export const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  headers: { "Content-Type": "application/json" },
});

function attachInterceptors(client: AxiosInstance) {
  // Request
  client.interceptors.request.use(
    async (config) => {
      const apiBaseUrl = process.env.REACT_APP_API_URL;
      let isApiRequest =
        config.url?.startsWith("/api") ||
        config.url?.startsWith(`${apiBaseUrl}/api`);

      // Check if baseURL matches API URL host
      if (!isApiRequest && config.baseURL && apiBaseUrl) {
        try {
          const apiUrl = new URL(apiBaseUrl);
          isApiRequest = config.baseURL.includes(apiUrl.host);
        } catch (error) {
          console.warn("Invalid API URL in environment:", apiBaseUrl);
        }
      }

      if (isApiRequest) {
        const impersonateUserId =
          typeof window !== "undefined"
            ? sessionStorage.getItem("impersonateUserId")
            : null;
        const isAdminApi =
          (config.url && config.url.includes("/api/admin")) || false;

        if (impersonateUserId && !isAdminApi) {
          const adminToken = localStorage.getItem("adminToken");
          if (adminToken) {
            config.headers = config.headers || {};
            (config.headers as any).Authorization = `Bearer ${adminToken}`;
            (config.headers as any)["X-Impersonate-User-Id"] = impersonateUserId;
            return config;
          }
        }

        if (getAccessTokenSilently) {
          try {
            const token = await getAccessTokenSilently();
            config.headers = config.headers || {};
            (config.headers as any).Authorization = `Bearer ${token}`;
          } catch (err) {
            console.warn("Failed to get Auth0 token:", err);
          }
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response (token refresh + page token expired handler)
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};

      // Page access token expired (FB page), prompt reconnect
      if (
        error.response?.status === 401 &&
        error.response?.data?.error === "page_token_expired"
      ) {
        if (!originalRequest._handledPageExpiry) {
          originalRequest._handledPageExpiry = true;
          const reauthUrl =
            error.response?.data?.reauthUrl || "/facebook/oauth/start";
          const proceed = window.confirm(
            "Your Facebook Page connection expired or was revoked. Reconnect now?"
          );
          if (proceed) window.location.href = reauthUrl;
        }
        return Promise.reject(error);
      }

      // Auth0 access token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // If impersonating, clear and bounce admin back instead of refreshing Auth0
        if (
          typeof window !== "undefined" &&
          sessionStorage.getItem("impersonateUserId")
        ) {
          sessionStorage.removeItem("impersonateUserId");
          sessionStorage.removeItem("impersonateUserName");
          window.location.href = "/admin/users";
          return Promise.reject(error);
        }

        try {
          if (getAccessTokenSilently) {
            const newToken = await getAccessTokenSilently();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client(originalRequest);
          }
        } catch (tokenError) {
          console.error("Failed to refresh token:", tokenError);
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Initialize the axios interceptor with Auth0 token getter
 * Call this once in your App.tsx after Auth0 is initialized
 */
export const initializeAxiosInterceptor = (
  tokenGetter: () => Promise<string>
) => {
  getAccessTokenSilently = tokenGetter;

  // Attach to both default axios and apiClient
  attachInterceptors(axios);
  attachInterceptors(apiClient);

  console.log(
    "✅ Axios interceptor initialized with automatic Auth0 token injection"
  );
};

export default apiClient;
